import React, {useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {ButtonDropdownField, DropdownList, Modal, SubHeading, Text} from '@forecast-it/design-system';
import {MODAL_TYPE, showModal} from '../../../../shared/components/modals/generic_modal_conductor';
import {ModalProps} from '../../../../shared/components/modals/types';
import {DeliverableDraft} from './baselineTemplateDraft';

export type ChangeTemplateDeliverableModalPayload = {
	/** The template deliverables the card can change to: every one except its own. */
	options: DeliverableDraft[];
	/** The card's current deliverable. Null or omitted for the card without a deliverable. */
	current?: DeliverableDraft | null;
	onChange: (deliverableKey: string) => void;
};

export const showChangeTemplateDeliverableModal = (props: ChangeTemplateDeliverableModalPayload): void => {
	showModal({type: MODAL_TYPE.CHANGE_TEMPLATE_DELIVERABLE, ...props});
};

/** Picks the deliverable a card's lines move to, from a dropdown, as the Baseline's Change Deliverable modal does. */
export const ChangeTemplateDeliverableModal = ({
	options,
	current,
	onChange,
	closeModal,
}: ModalProps<ChangeTemplateDeliverableModalPayload>): JSX.Element => {
	const {formatMessage} = useIntl();
	const [selected, setSelected] = useState<DeliverableDraft | null>(null);
	// Each option keeps its position in the full list, so its test id does not shift when the pick is filtered out.
	const choices = useMemo(
		() => options.map((option, index) => ({option, index})).filter(({option}) => option.key !== selected?.key),
		[options, selected]
	);
	const selectLabel = formatMessage({id: 'deliverable.select_another_deliverable'});
	return (
		<Modal size={Modal.SIZE.SMALL} onCloseModal={closeModal}>
			<Modal.Title>{formatMessage({id: 'deliverable.change_deliverable'})}</Modal.Title>
			<Modal.Content>
				<Text>{formatMessage({id: 'deliverable.change_deliverable.description'})}</Text>
				<SubHeading>{formatMessage({id: 'deliverable.change_deliverable.current_selected'})}</SubHeading>
				<Text>{current?.name ?? formatMessage({id: 'common.none'})}</Text>
				<SubHeading>{selectLabel}</SubHeading>
				<DropdownList>
					<DropdownList.Trigger data-cy="change-template-deliverable-select" aria-label={selectLabel}>
						<ButtonDropdownField isDefaultLabel={!selected}>
							{selected && (
								<Text ellipsis size="1">
									{selected.name}
								</Text>
							)}
						</ButtonDropdownField>
					</DropdownList.Trigger>
					{choices.length > 0 && (
						<DropdownList.Content offset={10} useTriggerWidth>
							{choices.map(({option, index}) => (
								<DropdownList.Item
									key={option.key}
									data-cy={`change-template-deliverable-option-${index}`}
									onClick={() => setSelected(option)}
								>
									{option.name}
								</DropdownList.Item>
							))}
						</DropdownList.Content>
					)}
				</DropdownList>
			</Modal.Content>
			<Modal.Footer>
				<Modal.PrimaryFooterButton
					onPress={() => {
						if (selected) {
							onChange(selected.key);
							closeModal();
						}
					}}
					disabled={!selected}
					dataCy="change-template-deliverable-confirm"
				>
					{formatMessage({id: 'common.confirm'})}
				</Modal.PrimaryFooterButton>
				<Modal.SecondaryFooterButton onPress={closeModal} dataCy="change-template-deliverable-cancel">
					{formatMessage({id: 'common.go_back'})}
				</Modal.SecondaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};
